import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import moment from 'moment';
import { FaExclamationTriangle, FaFlagCheckered, FaPercent } from 'react-icons/fa';
import { Button, FormField, Select, controlClass, IdChip } from '../../components/EntityList';
import { isRetroactiveClose } from '../../components/CloseContractModal/CloseContractModal';
import { showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const CLOSE_REASONS = [
	{ value: 'Acuerdo de partes', label: 'Acuerdo de partes' },
	{ value: 'Vencimiento de contrato', label: 'Vencimiento de contrato' },
	{ value: 'Incumplimiento', label: 'Incumplimiento' },
	{ value: 'Venta del inmueble', label: 'Venta del inmueble' },
];

/**
 * One modal, two modes, per HANDOFF-alquileres-list.md §7 — replaces the
 * legacy Ajustar/Finalizar inline modals in ContractList.jsx. Both modes
 * share the affected-contracts preview at the top.
 */
function ContractBulkActionModal({ show, mode, contracts, ipc, onClose, onConfirmAdjust, onConfirmClose }) {
	const { t } = useLabels();
	const [adjustMode, setAdjustMode] = useState('percent');
	const [adjustValue, setAdjustValue] = useState('');
	const [effectiveFrom, setEffectiveFrom] = useState(moment.utc().format('YYYY-MM-DD'));
	const [closeDate, setCloseDate] = useState(moment.utc().format('YYYY-MM-DD'));
	const [closeReason, setCloseReason] = useState(CLOSE_REASONS[0].value);

	useEffect(() => {
		if (show && mode === 'adjust') {
			setAdjustMode('percent');
			setAdjustValue(ipc || '');
			setEffectiveFrom(moment.utc().format('YYYY-MM-DD'));
		}
		if (show && mode === 'close') {
			setCloseDate(moment.utc().format('YYYY-MM-DD'));
			setCloseReason(CLOSE_REASONS[0].value);
		}
	}, [show, mode, ipc]);

	const title = mode === 'adjust' ? 'Ajustar alquileres' : 'Finalizar alquileres';
	const Icon = mode === 'adjust' ? FaPercent : FaFlagCheckered;
	const retroactive = mode === 'close' && isRetroactiveClose(closeDate);

	return (
		<BsModal
			show={show}
			onHide={onClose}
			centered
			dialogClassName='tw:max-w-[520px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<Icon />
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{title}
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
							{contracts.length} {contracts.length === 1 ? 'contrato seleccionado' : 'contratos seleccionados'}
						</p>
					</div>
				</div>
			</BsModal.Header>
			<BsModal.Body className='tw:px-[22px] tw:py-[18px]'>
				<div className='tw:border tw:border-border tw:rounded-token tw:bg-surface tw:overflow-y-auto tw:mb-4' style={{ maxHeight: 168 }}>
					{contracts.map((c) => (
						<div
							key={c._id}
							className='tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:border-b tw:border-border tw:last:border-b-0 tw:text-[13px]'
						>
							<IdChip>{c.customId}</IdChip>
							<span className='tw:flex-1 tw:truncate tw:text-ink-1'>{c.property?.name}</span>
							<span className='tw:font-mono tw:text-[13px]'>
								{showCurrency(c.currency)} {Number(c.amount || 0).toLocaleString('es-UY')}
							</span>
						</div>
					))}
				</div>

				{mode === 'adjust' && (
					<div className='tw:flex tw:flex-col tw:gap-3.5'>
						<div className='tw:inline-flex tw:rounded-[7px] tw:border tw:border-border tw:overflow-hidden tw:self-start'>
							{[
								{ value: 'percent', label: 'Porcentaje (%)' },
								{ value: 'fixed', label: 'Monto fijo' },
							].map((seg) => (
								<button
									key={seg.value}
									type='button'
									onClick={() => setAdjustMode(seg.value)}
									className={
										seg.value === adjustMode
											? 'tw:px-3 tw:py-1.5 tw:text-[12.5px] tw:font-medium tw:border-0 tw:bg-accent tw:text-white'
											: 'tw:px-3 tw:py-1.5 tw:text-[12.5px] tw:font-medium tw:border-0 tw:bg-surface tw:text-ink-2'
									}
								>
									{seg.label}
								</button>
							))}
						</div>
						<FormField label='Valor del ajuste' required>
							<input
								type='number'
								step='0.01'
								placeholder={adjustMode === 'percent' ? 'Ej: 8.5' : 'Ej: 25000'}
								value={adjustValue}
								onChange={(e) => setAdjustValue(e.target.value)}
								className={`${controlClass} tw:w-full tw:px-3`}
							/>
						</FormField>
						<FormField label='Vigente desde' required>
							<input
								type='date'
								value={effectiveFrom}
								onChange={(e) => setEffectiveFrom(e.target.value)}
								className={`${controlClass} tw:w-full tw:px-3`}
							/>
						</FormField>
						<p className='tw:text-[12.5px] tw:text-ink-3 tw:m-0'>
							Este cambio se aplicará solo a partir de las próximas cuotas generadas.
						</p>
					</div>
				)}

				{mode === 'close' && (
					<div className='tw:flex tw:flex-col tw:gap-3.5'>
						<FormField label='Fecha de finalización' required>
							<input
								type='date'
								value={closeDate}
								onChange={(e) => setCloseDate(e.target.value)}
								className={`${controlClass} tw:w-full tw:px-3`}
							/>
						</FormField>
						<FormField label='Motivo' required>
							<Select
								options={CLOSE_REASONS}
								value={closeReason}
								onChange={(e) => setCloseReason(e.target.value)}
							/>
						</FormField>
						{retroactive && (
							<div
								role='alert'
								className='tw:flex tw:items-start tw:gap-2.5 tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'
							>
								<span className='tw:mt-0.5 tw:shrink-0 tw:text-warn'>
									<FaExclamationTriangle />
								</span>
								<span>{t('msg.closeContractWarning')}</span>
							</div>
						)}
					</div>
				)}
			</BsModal.Body>
			<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
				<Button variant='default' onClick={onClose}>
					{t('action.cancel')}
				</Button>
				{mode === 'adjust' && (
					<Button
						variant='primary'
						icon={<FaPercent />}
						onClick={() => onConfirmAdjust({ mode: adjustMode, value: adjustValue, effectiveFrom })}
					>
						Aplicar ajuste
					</Button>
				)}
				{mode === 'close' && (
					<Button
						variant='primary'
						icon={<FaFlagCheckered />}
						onClick={() => onConfirmClose({ closeDate, closeReason })}
					>
						{retroactive ? t('action.closeContractRetroactive') : 'Finalizar alquileres'}
					</Button>
				)}
			</BsModal.Footer>
		</BsModal>
	);
}

ContractBulkActionModal.propTypes = {
	show: PropTypes.bool.isRequired,
	mode: PropTypes.oneOf(['adjust', 'close']),
	contracts: PropTypes.array.isRequired,
	ipc: PropTypes.number,
	onClose: PropTypes.func.isRequired,
	onConfirmAdjust: PropTypes.func.isRequired,
	onConfirmClose: PropTypes.func.isRequired,
};

export default ContractBulkActionModal;
